/**
 * CompetitionPanel — competitor/inspiration profiles as a visible workspace
 * panel. This used to hide at the bottom of the Setup tab; it lives on the
 * home page because the SMM jumps from here straight to competitor accounts
 * to see what they are doing.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { PlusIcon, ClientIcon, EditIcon, TrashIcon, LinkIcon } from "@/components/icons";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { PanelList } from "@/components/ui/PanelList";
import { PanelListItem } from "@/components/ui/PanelListItem";
import { PanelFilterTabs } from "@/components/ui/PanelFilterTabs";
import {
  CompetitionProfile,
  PLATFORM_CONFIG,
  PROFILE_TYPE_CONFIG,
  CreateCompetitionProfileRequest,
  UpdateCompetitionProfileRequest,
} from "@/domains/Competition/types";
import { competitionService } from "@/domains/Competition/services/competitionService";
import { CompetitionProfileModal } from "@/domains/Competition/components/CompetitionProfileModal";

interface CompetitionPanelProps {
  clientId: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export const CompetitionPanel: React.FC<CompetitionPanelProps> = ({
  clientId,
  collapsed,
  onToggleCollapse,
}) => {
  const [profiles, setProfiles] = useState<CompetitionProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<CompetitionProfile | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<CompetitionProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>("all");

  const fetchProfiles = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await competitionService.getProfiles(clientId);
      setProfiles(data);
    } catch (err) {
      console.error("Failed to fetch competition profiles:", err);
      setError("Failed to load competition profiles");
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleAddClick = () => {
    setEditingProfile(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (profile: CompetitionProfile) => {
    setEditingProfile(profile);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (profile: CompetitionProfile) => {
    setProfileToDelete(profile);
    setShowDeleteConfirm(true);
  };

  const handleModalSubmit = async (
    data: CreateCompetitionProfileRequest | UpdateCompetitionProfileRequest
  ) => {
    setIsSaving(true);
    try {
      if (editingProfile) {
        await competitionService.updateProfile(
          editingProfile.id,
          data as UpdateCompetitionProfileRequest
        );
      } else {
        await competitionService.createProfile(
          clientId,
          data as CreateCompetitionProfileRequest
        );
      }
      await fetchProfiles();
      setIsModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!profileToDelete) return;

    setIsDeleting(true);
    try {
      await competitionService.deleteProfile(profileToDelete.id);
      await fetchProfiles();
      setShowDeleteConfirm(false);
      setProfileToDelete(null);
    } catch (err) {
      console.error("Failed to delete profile:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const typesPresent = useMemo(
    () => Array.from(new Set(profiles.map((p) => p.profileType))),
    [profiles]
  );

  const sortedProfiles = useMemo(
    () =>
      [...profiles].sort(
        (a, b) =>
          a.platform.localeCompare(b.platform) ||
          a.displayName.localeCompare(b.displayName)
      ),
    [profiles]
  );

  const summary =
    profiles.length === 0
      ? "none yet"
      : `${profiles.length} profile${profiles.length === 1 ? "" : "s"}`;

  return (
    <>
      <WorkspacePanel
        id="competition"
        title="Competition"
        icon={<ClientIcon className="w-4 h-4" />}
        summary={summary}
        loading={isLoading}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        className={collapsed ? "" : "h-full"}
        bodyClassName="min-h-[48rem] max-h-[48rem]"
        subheader={
          typesPresent.length >= 2 ? (
            <PanelFilterTabs
              options={[
                { id: "all", label: "All", count: profiles.length },
                ...typesPresent.map((t) => ({
                  id: t,
                  label: PROFILE_TYPE_CONFIG[t].label,
                  count: profiles.filter((p) => p.profileType === t).length,
                })),
              ]}
              activeId={typeFilter}
              onChange={setTypeFilter}
            />
          ) : undefined
        }
        actions={
          <PanelHeaderAction
            label="Add"
            icon={<PlusIcon className="w-4 h-4" />}
            onClick={handleAddClick}
          />
        }
      >
        <div className="flex flex-col h-full gap-3">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              {error}
            </div>
          )}

          {!error && profiles.length === 0 && (
            <div className="flex flex-col items-center justify-center text-center py-8 px-6 rounded-xl border border-dashed border-gray-200 bg-gray-50/60">
              <p className="text-sm text-gray-600 mb-3">No competition profiles yet</p>
            </div>
          )}

          {sortedProfiles.length > 0 && (
            <PanelList
              items={sortedProfiles.filter(
                (p) => typeFilter === "all" || p.profileType === typeFilter
              )}
              keyFor={(p) => p.id}
              className="flex-1 h-auto"
              renderItem={(profile) => (
                <PanelListItem
                  leading={
                    <span className="block w-8 text-[10px] font-bold text-gray-500 bg-gray-100 rounded-md px-1.5 py-1 text-center">
                      {PLATFORM_CONFIG[profile.platform].icon}
                    </span>
                  }
                  title={profile.displayName}
                  meta={
                    <>
                      {profile.handle && (
                        <span className="text-xs text-gray-400 truncate">
                          @{profile.handle.replace(/^@/, "")}
                        </span>
                      )}
                      <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600">
                        {PROFILE_TYPE_CONFIG[profile.profileType].label}
                      </span>
                    </>
                  }
                  onPress={
                    profile.url
                      ? () => window.open(profile.url as string, "_blank", "noreferrer")
                      : undefined
                  }
                  trailing={
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      {profile.url && (
                        <LinkIcon className="w-3.5 h-3.5 text-gray-300" />
                      )}
                      <button
                        type="button"
                        onClick={() => handleEditClick(profile)}
                        aria-label={`Edit ${profile.displayName}`}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded"
                      >
                        <EditIcon className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(profile)}
                        aria-label={`Delete ${profile.displayName}`}
                        className="p-1 text-gray-400 hover:text-red-600 rounded"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  }
                />
              )}
            />
          )}
        </div>
      </WorkspacePanel>

      <CompetitionProfileModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleModalSubmit}
        profile={editingProfile}
        isLoading={isSaving}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setProfileToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Profile"
        message={`Are you sure you want to delete "${profileToDelete?.displayName}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={isDeleting}
      />
    </>
  );
};
